import { snapRoundDecimal } from '@calculations/round/decimal';
import { CraftProductOptions } from '@snap-types/models/craft/product-options';
import { SelectedProductOption } from '@snap-types/models/snap-product-options';
import { Decimal, ZERO } from '@snap-types/types/decimal';

import { calculateProductionOptionPriceAddition } from './price-addition';

export type CalculateProductOptionsPriceAdditionParams = {
  quantity: Decimal;
  productOptions: SelectedProductOption[];
  craftProductOptions: CraftProductOptions;
};

export type CalculateProductOptionsPriceAdditionSuccessResult = {
  isSuccess: true;
  unitPriceAddition: Decimal;
  changesUnitPrice: boolean;
};

export type CalculateProductOptionsPriceAdditionFailureResult = {
  isSuccess: false;
  error: string;
};

export type CalculateProductOptionsPriceAdditionResult =
  | CalculateProductOptionsPriceAdditionSuccessResult
  | CalculateProductOptionsPriceAdditionFailureResult;

export function calculate({
  quantity,
  productOptions,
  craftProductOptions,
}: CalculateProductOptionsPriceAdditionParams): CalculateProductOptionsPriceAdditionResult {
  if (!productOptions.length) {
    return {
      isSuccess: true,
      unitPriceAddition: ZERO,
      changesUnitPrice: true,
    };
  }

  let changesUnitPrice = false;

  const results: { hasCode: boolean; unitPriceAddition: Decimal }[] = [];

  for (const productOption of productOptions) {
    const optionGroup = craftProductOptions.optionGroups.find(
      (group) => group.name === productOption.groupName,
    );

    if (!optionGroup) {
      return {
        isSuccess: false,
        error: `No option group found for ${productOption.groupName}`,
      };
    }

    const option = optionGroup.options.find(
      (option) => option.name === productOption.optionName,
    );

    if (!option) {
      return {
        isSuccess: false,
        error: `No option found for ${productOption.optionName}`,
      };
    }

    const additionResult = calculateProductionOptionPriceAddition({
      weight: craftProductOptions.weight,
      option,
      quantity,
    });

    if (!additionResult.isSuccess) {
      return {
        isSuccess: false,
        error: `Failed to calculate addition for ${productOption.optionName}: ${additionResult.error}`,
      };
    }

    results.push({
      hasCode: !!option.code,
      unitPriceAddition: additionResult.unitPriceAddition,
    });
  }

  if (
    results.every(({ hasCode, unitPriceAddition }) => {
      return !hasCode || unitPriceAddition.gt(ZERO);
    })
  ) {
    changesUnitPrice = true;
  }

  const unitPriceAddition = snapRoundDecimal(
    Decimal.sum(...results.map(({ unitPriceAddition }) => unitPriceAddition)),
  );

  return {
    isSuccess: true,
    unitPriceAddition,
    changesUnitPrice,
  };
}
